<template>
  <section class="ev-achievements-section">
    <div class="container">
      <div class="section-header">
        <div class="section-tag">
          <i :class="headerData.icon"></i>
          <span>{{ headerData.tag }}</span>
        </div>
        <h2>{{ headerData.title }}</h2>
        <p class="section-description">{{ headerData.description }}</p>
      </div>
    </div>
    
    <div class="ev-achievements-slider swiper" ref="evAchievementsSwiper">
      <div class="swiper-wrapper">
        <div class="swiper-slide" v-for="(achievement, index) in achievementsData" :key="index">
          <div class="achievement-card">
            <div class="achievement-image-container">
              <img :src="achievement.image" :alt="achievement.title" class="achievement-image" />
              <div class="achievement-overlay">
                <div class="green-badge">
                  <i :class="badgeData.icon"></i>
                  <span>{{ badgeData.text }}</span>
                </div>
                <div class="achievement-content">
                  <h3 class="achievement-title">{{ achievement.title }}</h3>
                  <p class="achievement-category">{{ achievement.category }}</p>
                  <p class="achievement-date">{{ achievement.date }}</p>
                  <div class="achievement-tags">
                    <span v-for="(tag, tagIndex) in achievement.tags" :key="tagIndex" class="achievement-tag">{{ tag }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { onMounted, ref } from 'vue'

export default {
  name: "FullScreenSmoothSliderComp2",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-leaf",
        tag: "GREEN ACHIEVEMENTS",
        title: "EV Excellence Awards",
        description: "Recognized for our outstanding contributions to electric vehicle innovation and sustainable transportation"
      })
    },
    achievementsData: {
      type: Array,
      required: true,
      default: () => []
    },
    badgeData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-leaf",
        text: "Green Winner"
      })
    }
  },
  setup() {
    const evAchievementsSwiper = ref(null)

    const initEVAchievementsSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            evAchievementsSwiper.value = new window.Swiper('.ev-achievements-slider', {
              loop: true,
              autoplay: {
                delay: 0,
                disableOnInteraction: false,
              },
              speed: 5000,
              effect: 'slide',
              allowTouchMove: false,
              breakpoints: {
                0: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
                1200: {
                  slidesPerView: 4,
                  spaceBetween: 30,
                }
              }
            });
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    }

    onMounted(() => {
      initEVAchievementsSwiper()
    })

    return {
      evAchievementsSwiper
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* EV Achievements Section */
.ev-achievements-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #0a0f1c 0%, #1a2332 50%, #0a0f1c 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.ev-achievements-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, 
    rgba(0, 255, 150, 0.05) 0%, 
    rgba(0, 255, 200, 0.03) 25%, 
    rgba(0, 255, 100, 0.05) 50%, 
    rgba(0, 255, 150, 0.03) 75%, 
    rgba(0, 255, 200, 0.05) 100%);
  pointer-events: none;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 2;
}

.section-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 255, 150, 0.1);
  border: 1px solid rgba(0, 255, 150, 0.3);
  padding: 8px 16px;
  border-radius: 25px;
  margin-bottom: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #00ff96;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 15px 0;
  font-family: 'Archivo', sans-serif;
}

.section-description {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 30px 0;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.ev-achievements-slider {
  width: 100%;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.achievement-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 150, 0.2);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  margin: 0 10px;
}

.achievement-card:hover {
  transform: translateY(-5px);
  border-color: rgba(0, 255, 150, 0.4);
  box-shadow: 0 15px 40px rgba(0, 255, 150, 0.1);
}

.achievement-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.achievement-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.achievement-card:hover .achievement-image {
  transform: scale(1.05);
}

.achievement-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(0, 0, 0, 0.3) 0%, 
    rgba(0, 0, 0, 0.1) 50%, 
    rgba(0, 0, 0, 0.3) 100%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 15px;
  opacity: 0;
  transition: all 0.3s ease;
}

.achievement-card:hover .achievement-overlay {
  opacity: 1;
}

.green-badge {
  background: linear-gradient(135deg, #00ff96 0%, #00ff64 100%);
  color: #000;
  padding: 8px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(0, 255, 150, 0.3);
  align-self: flex-end;
  margin-bottom: 10px;
}

.green-badge i {
  font-size: 10px;
}

.achievement-content {
  width: 100%;
  margin-top: auto;
}

.achievement-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  line-height: 1.3;
  font-family: 'Archivo', sans-serif;
}

.achievement-category {
  font-size: 14px;
  color: #00ff96;
  margin: 0 0 5px 0;
  font-weight: 600;
}

.achievement-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 15px 0;
}

.achievement-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.achievement-tag {
  background: rgba(0, 255, 150, 0.1);
  color: #00ff96;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(0, 255, 150, 0.2);
}

/* Responsive Design */
@media (max-width: 991px) {
  .section-header h2 {
    font-size: 36px;
  }
  
  .section-description {
    font-size: 16px;
  }
}

@media (max-width: 767px) {
  .section-header h2 {
    font-size: 28px;
  }
  
  .section-description {
    font-size: 14px;
  }
  
  .ev-achievements-slider {
    padding: 0 10px;
  }
  
  .achievement-card {
    margin: 0 5px;
  }
  
  .achievement-image-container {
    height: 180px;
  }
  
  .achievement-title {
    font-size: 16px;
  }
}
</style>
